/** Trading wallets and permissions, billing, token, token trading and admin routes. */
import { z } from 'zod'
import { Address, Page } from '../../primitives.js'
import { Ok } from '../common.js'
import {
  AccountWallet,
  ClientOrderParams,
  CreatePermission,
  CreateWithdrawal,
  NarrowPermission,
  OrdersQuery,
  Permission,
  PermissionActionBody,
  PermissionOptions,
  PermissionRefParams,
  PermissionsQuery,
  PermissionTypedDataRequest,
  Portfolio,
  TradingWallet,
  TypedDataResult,
  WalletBalances,
  WalletOrder,
  WalletPositions,
  WalletPositionsQuery,
  WalletStatement,
  WalletStatementQuery,
  WithdrawalDraft,
  WithdrawalResult,
} from '../trading.js'
import { AgentTokenDetail, Claimable, LaunchToken, LaunchTokenResult, LinkToken } from '../tokens.js'
import { TokenQuoteQuery, TokenSwapBody, TokenSwapResult, TokenTradeQuote } from '../token-trade.js'
import { ChoosePlan, CreditsSummary, MySubscriptions, RateCard, SubscribeAgent, Topup, PaymentIntent, PaymentIntentResult, PaymentsQuery } from '../billing.js'
import { AgentStatusResult, JobEnqueued, ModerateAgent, UpsertRateCard } from '../manage.js'
import { HandleParams, IdParams } from './params.js'
import { defineRoute, type RouteDef } from './define.js'

export const tradingRoutes = {
  // --- The account's trading wallet and its permissions (session; arbitrage custody, ADR-0010) ---
  // One HD-custody wallet per account (decision 1, lane BOOCLEAN1): the routes name no wallet, there is one.
  createWallet: defineRoute({
    method: 'POST',
    path: '/wallet',
    auth: 'session',
    summary: "Request the account's trading wallet from arbitrage (creates the arbitrage customer on first use); derived asynchronously; once it has one, answers it",
    status: 202,
    response: TradingWallet,
  }),
  getWallet: defineRoute({ method: 'GET', path: '/wallet', auth: 'session', summary: "The account's trading wallet (refreshed from arbitrage), null until it asked for one", response: AccountWallet }),
  getWalletBalances: defineRoute({
    method: 'GET',
    path: '/wallet/balances',
    auth: 'session',
    summary: 'Wallet balances from arbitrage (native included; pending = committed to in-flight orders)',
    response: WalletBalances,
  }),
  getWalletPositions: defineRoute({
    method: 'GET',
    path: '/wallet/positions',
    auth: 'session',
    summary: 'Wallet lots per strategy and token from arbitrage',
    query: WalletPositionsQuery,
    response: WalletPositions,
  }),
  getWalletStatement: defineRoute({
    method: 'GET',
    path: '/wallet/statement',
    auth: 'session',
    summary: 'Deposits, withdrawals, fills, fees and gas from arbitrage (default: last 30 days)',
    query: WalletStatementQuery,
    response: WalletStatement,
  }),
  getPermissionOptions: defineRoute({
    method: 'GET',
    path: '/permissions/options',
    auth: 'session',
    summary: 'Venues, capabilities and quote tokens an authorization can name, and whether arbitrage is armed',
    response: PermissionOptions,
  }),
  buildPermissionTypedData: defineRoute({
    method: 'POST',
    path: '/permissions/typed-data',
    auth: 'session',
    summary: 'Exact EIP-712 typed data for AppPermission (grant) or PermissionAction (pause, resume, revoke) and its digest',
    body: PermissionTypedDataRequest,
    response: TypedDataResult,
  }),
  createPermission: defineRoute({
    method: 'POST',
    path: '/permissions',
    auth: 'session',
    summary: 'Relay a customer-signed AppPermission to arbitrage and record it',
    body: CreatePermission,
    status: 201,
    response: Permission,
  }),
  listPermissions: defineRoute({ method: 'GET', path: '/permissions', auth: 'session', summary: 'My permissions', query: PermissionsQuery, response: z.array(Permission) }),
  getPermission: defineRoute({ method: 'GET', path: '/permissions/:ref', auth: 'session', summary: 'One permission with usage', params: PermissionRefParams, response: Permission }),
  narrowPermission: defineRoute({
    method: 'POST',
    path: '/permissions/:ref/narrow',
    auth: 'session',
    summary: 'Narrow a permission (no customer signature needed; widening is PERMISSION_WIDENING)',
    params: PermissionRefParams,
    body: NarrowPermission,
    response: Permission,
  }),
  pausePermission: defineRoute({
    method: 'POST',
    path: '/permissions/:ref/pause',
    auth: 'session',
    summary: 'Pause a permission (optionally with the customer PermissionAction signature)',
    params: PermissionRefParams,
    body: PermissionActionBody,
    response: Permission,
  }),
  resumePermission: defineRoute({
    method: 'POST',
    path: '/permissions/:ref/resume',
    auth: 'session',
    summary: 'Resume a permission with the customer PermissionAction signature',
    params: PermissionRefParams,
    body: PermissionActionBody,
    response: Permission,
  }),
  revokePermission: defineRoute({
    method: 'POST',
    path: '/permissions/:ref/revoke',
    auth: 'session',
    summary: 'Revoke a permission (optionally with the customer PermissionAction signature)',
    params: PermissionRefParams,
    body: PermissionActionBody,
    response: Permission,
  }),
  buildWithdrawalTypedData: defineRoute({
    method: 'POST',
    path: '/withdrawals/typed-data',
    auth: 'session',
    summary: 'Exact EIP-712 Withdrawal typed data (with nonce and expiry to echo back) and its digest',
    body: WithdrawalDraft,
    response: TypedDataResult,
  }),
  createWithdrawal: defineRoute({
    method: 'POST',
    path: '/withdrawals',
    auth: 'session',
    summary: 'Relay a customer-signed withdrawal to arbitrage',
    body: CreateWithdrawal,
    status: 202,
    response: WithdrawalResult,
  }),
  listOrders: defineRoute({ method: 'GET', path: '/orders', auth: 'session', summary: 'Orders with fills and fees on my wallets, newest first', query: OrdersQuery, response: z.array(WalletOrder) }),
  getOrder: defineRoute({ method: 'GET', path: '/orders/:clientOrderId', auth: 'session', summary: 'One order by clientOrderId', params: ClientOrderParams, response: WalletOrder }),
  getMyPortfolio: defineRoute({ method: 'GET', path: '/me/portfolio', auth: 'session', summary: "The account's wallet with balances, lots and permissions", response: Portfolio }),

  // --- Billing (session) ---
  getMyCredits: defineRoute({ method: 'GET', path: '/me/credits', auth: 'session', summary: 'Credit envelopes', response: CreditsSummary }),
  topupCredits: defineRoute({
    method: 'POST',
    path: '/me/credits/topup',
    auth: 'session',
    summary: 'Prepaid credit top-up: USDG payment intent + [approve?, pay] txs; the prepaid envelope is created on confirmation',
    body: Topup,
    status: 201,
    response: PaymentIntentResult,
  }),
  choosePlan: defineRoute({
    method: 'POST',
    path: '/me/plan',
    auth: 'session',
    summary: 'Subscribe to a plan for one period: USDG payment intent + txs (free and stub intents activate at once)',
    body: ChoosePlan,
    response: PaymentIntentResult,
  }),
  subscribeAgent: defineRoute({
    method: 'POST',
    path: '/agents/:handle/subscribe',
    auth: 'session',
    summary: 'Subscribe to agent: USDG payment intent + txs (free tier activates at once)',
    params: HandleParams,
    body: SubscribeAgent,
    status: 201,
    response: PaymentIntentResult,
  }),
  listMyPayments: defineRoute({ method: 'GET', path: '/me/payments', auth: 'session', summary: 'Payment intents, newest first', query: PaymentsQuery, response: Page(PaymentIntent) }),
  getMyPayment: defineRoute({ method: 'GET', path: '/me/payments/:id', auth: 'session', summary: 'One payment intent (poll after sending the pay tx)', params: IdParams, response: PaymentIntent }),
  unsubscribeAgent: defineRoute({ method: 'DELETE', path: '/agents/:handle/subscribe', auth: 'session', summary: 'Cancel agent subscription', params: HandleParams, response: Ok }),
  listMySubscriptions: defineRoute({ method: 'GET', path: '/me/subscriptions', auth: 'session', summary: 'Plan and agent subscriptions', response: MySubscriptions }),

  // --- Token (session, owner) ---
  launchToken: defineRoute({
    method: 'POST',
    path: '/agents/:handle/token/launch',
    auth: 'session',
    owner: true,
    summary: 'The token launched by the platform with these terms (records the pending token); the creation fee as one payCreation tx when it is not paid yet',
    params: HandleParams,
    body: LaunchToken,
    status: 201,
    response: LaunchTokenResult,
  }),
  linkToken: defineRoute({
    method: 'POST',
    path: '/agents/:handle/token/link',
    auth: 'session',
    owner: true,
    summary: 'Link externally launched token (registry-verified)',
    params: HandleParams,
    body: LinkToken,
    response: AgentTokenDetail,
  }),
  getTokenClaimable: defineRoute({
    method: 'GET',
    path: '/agents/:handle/token/claimable',
    auth: 'session',
    summary: 'Per-epoch claimable amounts from chain',
    params: HandleParams,
    query: z.object({ holder: Address }),
    response: Claimable,
  }),

  // --- Token trading (holders, own wallet) ---
  getTokenQuote: defineRoute({
    method: 'GET',
    path: '/tokens/:address/quote',
    auth: 'public',
    summary: 'Exact-in buy/sell quote from the agent token launch pool (V4Quoter)',
    params: z.object({ address: Address }),
    query: TokenQuoteQuery,
    response: TokenTradeQuote,
  }),
  buildTokenSwap: defineRoute({
    method: 'POST',
    path: '/tokens/:address/swap',
    auth: 'session',
    summary: 'Unsigned wallet txs to buy or sell an agent token through the UniversalRouter',
    params: z.object({ address: Address }),
    body: TokenSwapBody,
    response: TokenSwapResult,
  }),

  // --- Admin (minimal) ---
  adminListRateCards: defineRoute({ method: 'GET', path: '/admin/rate-cards', auth: 'admin', summary: 'Rate cards', response: z.array(RateCard) }),
  adminUpsertRateCard: defineRoute({ method: 'POST', path: '/admin/rate-cards', auth: 'admin', summary: 'New rate card (closes previous)', body: UpsertRateCard, status: 201, response: RateCard }),
  adminModerateAgent: defineRoute({
    method: 'POST',
    path: '/admin/agents/:handle/moderate',
    auth: 'admin',
    summary: 'Set agent status',
    params: HandleParams,
    body: ModerateAgent,
    response: AgentStatusResult,
  }),
  adminReconcileFees: defineRoute({ method: 'POST', path: '/admin/fees/reconcile', auth: 'admin', summary: 'Enqueue fees.reconcile', status: 202, response: JobEnqueued }),
} as const satisfies Record<string, RouteDef>
